import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ResourcesHeader from "./components/ResourcesHeader";
import ResourcesSidebar from "./components/ResourcesSidebar";
import styles from "./ResourcesLayout.module.css";

/**
 * ResourcesLayout — Documentation shell.
 *
 * Renders the documentation header, sidebar, and child page content
 * via <Outlet />. Completely isolated from the main site's dark design.
 *
 * Layout:
 *   Desktop:  [Header]  →  [Sidebar | Content | TOC]
 *   Mobile:   [Header + hamburger]  →  [Content]  (sidebar = drawer)
 */
const ResourcesLayout = ({ user }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef(null);
  const { pathname } = useLocation();

  // Close sidebar when pressing Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  // Always start at the top when switching sections (main is the scroll container)
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [pathname]);

  return (
    <>
      <Helmet>
        <title>Resources — WebTwin</title>
        <meta
          name="description"
          content="WebTwin platform documentation. Learn about Interactive 3D, AR Experiences, 360° Virtual Tours, and platform management features."
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className={styles["docs-shell"]} id="resources-shell">
        {/* Header */}
        <ResourcesHeader user={user} />

        {/* Mobile hamburger bar */}
        <div className={styles["docs-mobile-bar"]}>
          <button
            className={styles["docs-mobile-menu-btn"]}
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
            id="resources-mobile-menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
            <span>Menu</span>
          </button>
        </div>

        {/* Main body: sidebar + content area */}
        <div className={styles["docs-body-layout"]}>
          <ResourcesSidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          <main className={styles["docs-main"]} ref={mainRef}>
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
};

export default ResourcesLayout;
